import { eventsApiBaseUrl } from "../config/eventsApi";
import { DEFAULT_WEBSITE_ABOUT_SECTIONS } from "../data/aboutContent";

const FETCH_TIMEOUT_MS = 8000;

/**
 * Admin About copy from GET /api/public/site-content/about.
 * Returns null when the body is missing or blank so the page can keep its fallback.
 */
const EMPHASIS_WRAPPERS = [
  ["***", "***"],
  ["___", "___"],
  ["**", "**"],
  ["__", "__"],
  ["*", "*"],
  ["_", "_"],
];

/** A whole line that is only bold and/or italic, not a heading or list. */
function isEmphasisLine(line) {
  const trimmed = line.trim();
  if (!trimmed) return false;
  if (/^(#{1,6}\s|[-*+]\s|\d+\.\s|>|```|~~~)/.test(trimmed)) return false;
  const wrapper = EMPHASIS_WRAPPERS.find(
    ([open, close]) =>
      trimmed.startsWith(open) &&
      trimmed.endsWith(close) &&
      trimmed.length > open.length + close.length,
  );
  if (!wrapper) return false;
  const inner = trimmed.slice(wrapper[0].length, -wrapper[1].length).trim();
  return inner.length > 0;
}

function takeLeadingEmphasis(lines) {
  let index = 0;
  while (index < lines.length && lines[index].trim() === "") index += 1;
  const collected = [];
  while (index < lines.length) {
    const trimmed = lines[index].trim();
    if (trimmed === "") {
      let next = index + 1;
      while (next < lines.length && lines[next].trim() === "") next += 1;
      if (next < lines.length && isEmphasisLine(lines[next])) {
        index = next;
        continue;
      }
      break;
    }
    if (!isEmphasisLine(trimmed)) break;
    collected.push(trimmed);
    index += 1;
  }
  return { lines: collected, next: index };
}

function takeTrailingEmphasis(lines, start) {
  let end = lines.length;
  while (end > start && lines[end - 1].trim() === "") end -= 1;
  const collected = [];
  while (end > start && isEmphasisLine(lines[end - 1])) {
    const previous = end - 2;
    if (previous >= start && lines[previous].trim() !== "") break;
    collected.unshift(lines[end - 1].trim());
    end -= 1;
    while (end > start && lines[end - 1].trim() === "") end -= 1;
  }
  return { lines: collected, end };
}

/**
 * Pull a leading bold/italic tagline and a trailing emphasis close off About
 * Markdown. Everything else, including mid-article emphasis, stays in the body.
 */
export function splitAboutProse(markdown) {
  const lines = String(markdown || "")
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n");
  const lead = takeLeadingEmphasis(lines);
  const close = takeTrailingEmphasis(lines, lead.next);
  return {
    lead: lead.lines.join("\n"),
    body: lines.slice(lead.next, close.end).join("\n").trim(),
    close: close.lines.join("\n\n"),
  };
}

const ABOUT_ICONS = new Set([
  "users",
  "target",
  "flag",
  "trending-up",
  "flower",
  "hand-heart",
  "trophy",
  "handshake",
]);
const OWNED_ABOUT_IMAGE =
  /^\/api\/public\/site-content\/about\/images\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function aboutImageSrc(path) {
  if (typeof path !== "string" || !OWNED_ABOUT_IMAGE.test(path)) return null;
  return `${eventsApiBaseUrl()}${path}`;
}

function boundedText(value, max) {
  return typeof value === "string" && value.length <= max;
}

function publicAboutSections(sections) {
  if (!sections || typeof sections !== "object") return null;
  const shortFields = ["tagline", "closing", "affiliation"];
  if (!shortFields.every((key) => boundedText(sections[key], 500))) return null;
  if (
    !["guidingHeading", "activitiesHeading"].every((key) =>
      boundedText(sections[key], 120),
    )
  )
    return null;
  const validIcon = (item) => item && ABOUT_ICONS.has(item.icon);
  if (
    !["whoWeAre", "vision", "purpose"].every(
      (key) =>
        validIcon(sections[key]) &&
        boundedText(sections[key].heading, 120) &&
        boundedText(sections[key].text, 4000),
    )
  )
    return null;
  if (
    !Array.isArray(sections.principles) ||
    sections.principles.length !== 3 ||
    !sections.principles.every(
      (item, index) =>
        item?.id === DEFAULT_WEBSITE_ABOUT_SECTIONS.principles[index].id &&
        boundedText(item.hindi, 120) &&
        boundedText(item.english, 120),
    )
  )
    return null;
  if (
    !Array.isArray(sections.activities) ||
    sections.activities.length !== 6 ||
    !sections.activities.every(
      (item, index) =>
        item?.id === DEFAULT_WEBSITE_ABOUT_SECTIONS.activities[index].id &&
        validIcon(item) &&
        boundedText(item.label, 120),
    )
  )
    return null;
  return sections;
}

export function publicAboutCopy(about) {
  if (!about || typeof about !== "object") return null;
  const bodyMarkdown = typeof about.bodyMarkdown === "string" ? about.bodyMarkdown : "";
  const sections = publicAboutSections(about.sections);
  if (!bodyMarkdown.trim() && !sections) return null;
  const title = typeof about.title === "string" ? about.title.trim() : "";
  return {
    title: title.length <= 120 ? title || null : null,
    bodyMarkdown,
    ...(sections ? { sections } : {}),
  };
}

async function readJson(res) {
  const text = await res.text();
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/** @returns {Promise<{ title: string|null, bodyMarkdown: string }|null>} */
export async function fetchPublicAbout({ signal } = {}) {
  const base = eventsApiBaseUrl();
  if (!base) return null;
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), FETCH_TIMEOUT_MS);
  const onAbort = () => ctrl.abort();
  signal?.addEventListener("abort", onAbort);
  try {
    const res = await fetch(`${base}/api/public/site-content/about`, {
      signal: ctrl.signal,
      headers: { Accept: "application/json" },
    });
    const data = await readJson(res);
    if (!res.ok || !data || data.ok !== true) return null;
    return publicAboutCopy(data.about);
  } finally {
    clearTimeout(timer);
    signal?.removeEventListener("abort", onAbort);
  }
}
